import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IGetSubscriptionItemSchema,
  IGetSubscriptionSchema,
  IRateResSchema,
  IUsageEvent,
  IUsageEventListRequestSchema,
  SubscriptionBillingStatusEnum,
  UsageEventListRequestSchema,
} from '@monetize/types/app';
import {
  ApiListInfiniteScrollResponse,
  DEFAULT_PAGER,
  FilterType,
  FilterTypeOperator,
  TDataTablePager,
} from '@monetize/types/common';
import { replaceUserTimezoneWithUtc } from '@monetize/utils/core';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { addDays } from 'date-fns/addDays';
import { endOfDay } from 'date-fns/endOfDay';
import { formatISO } from 'date-fns/formatISO';
import { getUnixTime } from 'date-fns/getUnixTime';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleApiErrorToast } from '../../api/axios';
import { useGetById } from '../../api/queryUtils';
import { useGetUsageRecordsInfiniteLoad } from '../../api/usageService';
import {
  getAccountDetailRoute,
  getSubscriptionOverviewRoute,
} from '../../constants/routes';
import { useACL } from '../../services/acl/acl';

interface UseSubscriptionUsageParams {
  accountId: string;
  subscriptionId: string;
  productId: string;
}

interface UseSubscriptionUsageReturn {
  formData: UseFormReturn<IUsageEventListRequestSchema>;
  getUsageRecordsQuery: UseInfiniteQueryResult<
    ApiListInfiniteScrollResponse<IUsageEvent>
  >;
  isUsageFormOpen: boolean;
  isManualUsageModalOpen: boolean;
  usageRecords: IUsageEvent[];
  totalElements: number;
  filters: FilterType[];
  isCanceled: boolean;
  subscription: IGetSubscriptionSchema | undefined;
  subscriptionItem: IGetSubscriptionItemSchema | null;
  rate: IRateResSchema | undefined;
  isLoadingSubscription: boolean;
  activeUsageRecord: IUsageEvent | undefined;
  canUpdateUsage: boolean;
  pager: TDataTablePager;
  earliestAllowedEditDate: number;
  onOpenUsageForm: () => void;
  onCloseUsageForm: () => void;
  onOpenUsageUpload: () => void;
  onCloseUsageUpload: () => void;
  setActiveUsageRecord: (usageRecord: IUsageEvent | undefined) => void;
  setSubscriptionItem: (
    subscriptionItem: IGetSubscriptionItemSchema | null,
  ) => void;
}

export const useSubscriptionUsage = ({
  accountId,
  subscriptionId,
  productId,
}: UseSubscriptionUsageParams): UseSubscriptionUsageReturn => {
  const navigate = useNavigate();
  const { canDo } = useACL();

  const canUpdateUsage = canDo([['billing', 'update']]);

  // Modal Controls
  const {
    open: isManualUsageModalOpen,
    onClose: onCloseUsageUpload,
    onOpen: onOpenUsageUpload,
  } = useDisclosure();

  const {
    open: isUsageFormOpen,
    onOpen: onOpenUsageForm,
    onClose: onCloseUsageForm,
  } = useDisclosure();

  // State Management
  const [earliestAllowedEditDate, setEarliestAllowedEditDate] =
    useState<number>(() => getUnixTime(addDays(new Date(), -30)));
  const [subscriptionItem, setSubscriptionItem] =
    useState<IGetSubscriptionItemSchema | null>(null);
  const [activeUsageRecord, setActiveUsageRecord] = useState<
    IUsageEvent | undefined
  >();
  const [pager] = useState<TDataTablePager>(() => ({
    ...DEFAULT_PAGER,
    rows: 100,
    sortField: 'timestamp',
    sortOrder: 1,
  }));

  const periodDefaultStartDate = replaceUserTimezoneWithUtc(
    formatISO(startOfDay(addDays(new Date(), -30))),
  ).toISOString();

  const periodDefaultEndDate = replaceUserTimezoneWithUtc(
    formatISO(endOfDay(new Date())),
  ).toISOString();

  const formData = useForm<IUsageEventListRequestSchema>({
    resolver: zodResolver(UsageEventListRequestSchema),
    mode: 'onChange',
    defaultValues: {
      subscriptionId,
      usageTypeIds: [],
      startTime: periodDefaultStartDate,
      endTime: periodDefaultEndDate,
    },
  });

  const { setValue, getValues, watch, formState } = formData;

  // Watch form fields to trigger re-renders
  watch('startTime');
  watch('endTime');
  watch('usageTypeIds');

  const { data: subscription, isLoading: isLoadingSubscription } =
    useGetById<IGetSubscriptionSchema>('accountSubscriptions', subscriptionId, {
      enabled: !!subscriptionId,
      refetchOnWindowFocus: false,
      meta: {
        showErrorToast: true,
        navigateTo: getAccountDetailRoute(accountId),
      },
    });

  useEffect(() => {
    if (subscription) {
      const subscriptionItemTemp = subscription?.subscriptionItems.find(
        (item) => item.product.id === productId,
      );
      if (subscriptionItemTemp) {
        setSubscriptionItem(subscriptionItemTemp);

        const startTime = startOfDay(
          parseISO(subscription.usagePeriodStart ?? periodDefaultStartDate),
        );
        const startTimeUTC = startOfDay(
          new Date(
            Date.UTC(
              startTime.getUTCFullYear(),
              startTime.getUTCMonth(),
              startTime.getUTCDate(),
            ),
          ),
        );

        setEarliestAllowedEditDate(getUnixTime(startTimeUTC));
        setValue('startTime', formatISO(startTime));

        setValue(
          'endTime',
          formatISO(
            endOfDay(
              parseISO(subscription.usagePeriodEnd ?? periodDefaultEndDate),
            ),
          ),
        );
      } else {
        handleApiErrorToast(
          `The provided product does not exist on the subscription.`,
        );
        navigate(getSubscriptionOverviewRoute(accountId, subscriptionId));
        return;
      }

      if (
        subscriptionItemTemp?.product.usageTypes &&
        subscriptionItemTemp?.product.usageTypes.length > 0
      ) {
        setValue(
          'usageTypeIds',
          subscriptionItemTemp?.product.usageTypes.map(
            (usageType) => usageType.id,
          ) || [],
        );
      } else {
        handleApiErrorToast(`The provided product is not a usage product.`);
        navigate(getSubscriptionOverviewRoute(accountId, subscriptionId));
        return;
      }
    }
  }, [
    accountId,
    navigate,
    periodDefaultEndDate,
    periodDefaultStartDate,
    productId,
    setValue,
    subscription,
    subscriptionId,
  ]);

  const isCanceled =
    subscription?.billingStatus === SubscriptionBillingStatusEnum.CANCELED;

  const { data: rate } = useGetById<IRateResSchema>(
    'productCatalogRates',
    subscription?.rateId!,
    { enabled: !!subscription?.rateId!, refetchOnWindowFocus: false },
  );

  const payload = getValues();
  const getUsageRecordsQuery = useGetUsageRecordsInfiniteLoad(
    {
      ...payload,
      startTime: replaceUserTimezoneWithUtc(payload.startTime).toISOString(),
      endTime: replaceUserTimezoneWithUtc(payload.endTime).toISOString(),
    },
    pager,
    {
      enabled:
        !!payload.subscriptionId &&
        (payload.usageTypeIds || []).length > 0 &&
        formState.isValid,
      meta: { showErrorToast: true },
    },
  );
  const { data: usageRecordsAllPages } = getUsageRecordsQuery;

  const usageRecords =
    usageRecordsAllPages?.pages.flatMap((page) => page.content) || [];
  const totalElements = usageRecordsAllPages?.pages?.[0].totalElements || 0;

  const filters = useMemo((): FilterType[] => {
    return [
      {
        key: 'startTime',
        value: replaceUserTimezoneWithUtc(payload.startTime).toISOString(),
        operator: FilterTypeOperator.EQUAL,
      },
      {
        key: 'endTime',
        value: replaceUserTimezoneWithUtc(payload.endTime).toISOString(),
        operator: FilterTypeOperator.EQUAL,
      },
      {
        key: 'subscriptionId',
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        value: payload.subscriptionId!,
        operator: FilterTypeOperator.EQUAL,
      },
      {
        key: 'usageTypeIds',
        value: (payload.usageTypeIds || []).join(','),
        operator: FilterTypeOperator.EQUAL,
      },
      {
        key: 'sort',
        value: `${pager.sortField}:${pager.sortOrder === 1 ? 'asc' : 'desc'}`,
        operator: FilterTypeOperator.EQUAL,
      },
    ];
  }, [
    payload.endTime,
    payload.startTime,
    payload.subscriptionId,
    payload.usageTypeIds,
    pager.sortField,
    pager.sortOrder,
  ]);

  return {
    formData,
    // FIXME: types are wrong here
    getUsageRecordsQuery: getUsageRecordsQuery as any,
    isUsageFormOpen,
    isManualUsageModalOpen,
    usageRecords,
    totalElements,
    filters,
    isCanceled,
    subscription,
    rate,
    isLoadingSubscription,
    activeUsageRecord,
    subscriptionItem,
    canUpdateUsage,
    pager,
    earliestAllowedEditDate,
    onOpenUsageForm,
    onCloseUsageForm,
    onOpenUsageUpload,
    onCloseUsageUpload,
    setActiveUsageRecord,
    setSubscriptionItem,
  };
};
